import equal from "deep-equal";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrayParam,
  BooleanParam,
  createEnumParam,
  QueryParamConfig,
  useQueryParams,
  withDefault
} from "use-query-params";

import ArchiveSegment from "@components/carePlan/ArchiveSegment";
import styles from "@components/carePlan/ExpertCarePlan.module.css";
import GoalEditor from "@components/carePlan/GoalEditor";
import GoalsManager from "@components/carePlan/GoalsManager";
import NextConsult from "@components/carePlan/NextConsult";
import ExpertGoalMenu from "@components/ExpertGoalMenu";
import { GoalSectionEnum } from "@typing/Enums";
import { GetJourneyWithPlanQuery, GoalFragment, GoalStatusEnum } from "@typing/Generated";
import { getAllGoals, sortByPosition } from "@utils/modelUtils";

type Journey = GetJourneyWithPlanQuery["journey"];

type Props = {
  journey: Journey;
};

const ExpertCarePlan = ({ journey }: Props) => {
  const sectionParam = createEnumParam<GoalSectionEnum>(Object.values(GoalSectionEnum));

  const [query, setQuery] = useQueryParams({
    allGoals: withDefault({ ...BooleanParam, urlName: "expertCarePlanAllGoals" }, true),
    currentSection: withDefault(
      { ...sectionParam, urlName: "expertCarePlanCurrentSection" },
      journey.goals.filter(g => !g.archived).length > 1 ? GoalSectionEnum.CARE_PARTNER_TASKS : GoalSectionEnum.ALL
    ),
    selectedGoalIds: withDefault(
      { ...ArrayParam, urlName: "expertCarePlanGoalIds" },
      sortByPosition(
        getAllGoals(journey).filter(g => !journey.editModeGoals?.some(goal => goal.activeGoalId === g.id))
      ).map(goal => goal.id)
    ) as QueryParamConfig<string[]>
  });

  const [allGoals, setAllGoals] = useState(query.allGoals);
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>(query.selectedGoalIds);
  const [overrideGoal, setOverrideGoal] = useState<GoalFragment | null>(null);
  const [currentSection, setCurrentSection] = useState<GoalSectionEnum>(query.currentSection);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const values = { allGoals, currentSection, selectedGoalIds };
    if (!equal(values, query)) {
      setQuery(values);
    }
  }, [allGoals, currentSection, query, selectedGoalIds, setQuery]);

  const [pollingGoalId, setPollingGoalId] = useState<string | null>(null);
  const [archived, setArchived] = useState(false);
  const goalEditorRef = useRef<HTMLDivElement>(null);

  const goals = sortByPosition(
    journey.goals
      .filter(goal => goal.archived === archived)
      .concat(journey.editModeGoals?.filter(goal => goal.archived === archived) ?? [])
  );

  const selectedGoals = useMemo(() => {
    const selected = sortByPosition(goals.filter(goal => selectedGoalIds.includes(goal.id)));

    const goal = selected[0];

    if ((selected.length === 0 || (selected.length === 1 && goal.status !== overrideGoal?.status)) && overrideGoal) {
      return [overrideGoal];
    }

    return selected;
  }, [goals, overrideGoal, selectedGoalIds]);

  useEffect(() => {
    const selected = sortByPosition(goals.filter(goal => selectedGoalIds.includes(goal.id)));

    if (overrideGoal && overrideGoal.status === selected[0]?.status) {
      setOverrideGoal(null);
    }
  }, [goals, overrideGoal, selectedGoalIds]);

  useEffect(() => {
    if (pollingGoalId && !selectedGoals.some(g => g.id === pollingGoalId)) {
      const interval = setInterval(() => {
        const goal = journey.goals.find(goal => goal.id === pollingGoalId);

        if (goal) {
          setPollingGoalId(null);
          setSelectedGoalIds([goal.id]);
          setCurrentSection(GoalSectionEnum.ALL);
        }
      }, 200);

      return () => clearInterval(interval);
    }
  }, [journey.goals, pollingGoalId, selectedGoals]);

  const handleGoalsSelect = (goals: GoalFragment[], allGoals: boolean) => {
    if (!allGoals) {
      setAllGoals(false);
    }
    setSelectedGoalIds(goals.map(g => g.id));

    if (goals.length === 1) {
      setOverrideGoal(goals[0]);
      setCurrentSection(GoalSectionEnum.ALL);
    } else {
      setOverrideGoal(null);
    }

    const boundingClient = goalEditorRef.current?.getBoundingClientRect();
    if (boundingClient && boundingClient.top < 0) {
      setTimeout(() => goalEditorRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  };

  const handleGoalCreate = () => {
    setAllGoals(false);
    setOverrideGoal(null);
    handleGoalsSelect([], false);
  };

  return (
    <div>
      <NextConsult journey={journey} />
      <div className={styles.expertGrid}>
        <div>
          <ArchiveSegment
            count={
              getAllGoals(journey).filter(goal => goal.archived !== archived && goal.status !== GoalStatusEnum.DRAFT)
                .length
            }
            setValue={setArchived}
            value={archived}
          />
          <ExpertGoalMenu
            allGoals={allGoals}
            archived={archived}
            handleGoalCreate={handleGoalCreate}
            handleGoalsSelect={handleGoalsSelect}
            journey={journey}
            loading={loading}
            selectedGoalIds={selectedGoalIds}
            setAllGoals={setAllGoals}
            setLoading={setLoading}
          />
        </div>

        <div className="full-width" ref={goalEditorRef}>
          {!allGoals &&
          (selectedGoals.length === 0 ||
            (selectedGoals.length === 1 && selectedGoals[0].status === GoalStatusEnum.DRAFT)) ? (
            <div className="full-width">
              <GoalEditor
                goal={selectedGoals.length ? selectedGoals[0] : null}
                journey={journey}
                loading={loading}
                setSelectedGoalId={setPollingGoalId}
              />
            </div>
          ) : (
            <div className="full-width">
              <GoalsManager
                currentSection={currentSection}
                goals={selectedGoals}
                journey={journey}
                loading={loading}
                setCurrentSection={setCurrentSection}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpertCarePlan;
